import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { MonacoJsonEditor } from '../../components/MonacoJsonEditor';
import { partnerApi, type ToolDefinitionInput } from '../../lib/api';
import styles from './ToolForm.module.css';

interface ToolFormProps {
  draft: ToolDefinitionInput;
  onChange: (next: ToolDefinitionInput) => void;
  isEditing?: boolean;
  onValidityChange?: (isValid: boolean) => void;
}

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
const AUTH_TYPES = ['custom_header', 'bearer', 'api_key', 'basic'] as const;
const SCOPES = ['partner_user', 'partner_user:verified', 'business', 'partner'] as const;

export const ToolForm = ({ draft, onChange, isEditing = false, onValidityChange }: ToolFormProps) => {
  const { data: credentialsData } = useQuery({
    queryKey: ['ai-credentials'],
    queryFn: () => partnerApi.getCredentials(),
  });

  const credentials = credentialsData?.credentials ?? [];

  const set = <K extends keyof ToolDefinitionInput>(key: K, value: ToolDefinitionInput[K]) => {
    onChange({ ...draft, [key]: value });
  };

  const [invalidJsonKeys, setInvalidJsonKeys] = useState<Set<string>>(new Set());
  const reportFieldValidity = (key: string, isValid: boolean) => {
    setInvalidJsonKeys(prev => {
      const wasInvalid = prev.has(key);
      if (isValid && !wasInvalid) return prev;
      if (!isValid && wasInvalid) return prev;
      const next = new Set(prev);
      if (isValid) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    onValidityChange?.(invalidJsonKeys.size === 0);
  }, [invalidJsonKeys, onValidityChange]);

  return (
    <div className={styles.form}>
      <Section title="Identity" subtitle="How the LLM agent and runtime address this tool.">
        <Field
          label="Display name"
          hint={
            isEditing
              ? 'Locked after creation. The runtime canonical name is derived from this and cannot be changed without archiving the tool.'
              : 'snake_case verb_object, e.g. create_patron'
          }>
          <input
            type="text"
            value={draft.display_name}
            onChange={e => set('display_name', e.target.value)}
            disabled={isEditing}
            className={styles.monoInput}
            placeholder="create_patron"
          />
        </Field>
        <Field label="Description" hint="LLM-facing one-liner describing what this tool does.">
          <textarea
            value={draft.description}
            onChange={e => set('description', e.target.value)}
            className={styles.textarea}
            rows={2}
            placeholder="Create a customer in your system with their phone, name, and email."
          />
        </Field>
        <Field label="Status">
          <select
            value={draft.status}
            onChange={e => set('status', e.target.value as 'active' | 'inactive')}
            className={styles.select}>
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </select>
        </Field>
        <AnnotationsEditor
          value={(draft.annotations ?? {}) as Record<string, boolean>}
          onChange={v => set('annotations', v as ToolDefinitionInput['annotations'])}
        />
      </Section>

      <Section
        title="HTTP"
        subtitle={
          <>
            Where to call. Templates use{' '}
            <a
              href="https://jsonata.org/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.subtitleLink}>
              JSONata
            </a>
            : {'{{args.x}}, {{variables.x}}'}.
          </>
        }>
        <Field label="Method">
          <select
            value={draft.http_method}
            onChange={e => set('http_method', e.target.value)}
            className={styles.monoSelect}>
            {HTTP_METHODS.map(m => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Base URL">
          <input
            type="text"
            value={draft.base_url}
            onChange={e => set('base_url', e.target.value)}
            className={styles.monoInput}
            placeholder="https://api.partner.com/v1"
          />
        </Field>
        <Field label="Path template">
          <input
            type="text"
            value={draft.path_template}
            onChange={e => set('path_template', e.target.value)}
            className={styles.monoInput}
            placeholder="/customers/{{customer_id}}"
          />
        </Field>
        <div className={styles.twoColumnGrid}>
          <Field label="Timeout (ms)">
            <input
              type="number"
              value={draft.timeout_ms}
              onChange={e => set('timeout_ms', Number(e.target.value))}
              className={styles.monoInput}
              min={100}
              max={60000}
            />
          </Field>
          <Field label="Required scope">
            <select
              value={draft.required_scope}
              onChange={e => set('required_scope', e.target.value)}
              className={styles.input}>
              {SCOPES.map(s => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Section>

      <Section
        title="Auth & credentials"
        subtitle="Pick which credentials this tool can use. Each gets a label (slot) unique to this tool — reference it in mappings as {{secrets.<slot>}}.">
        <Field label="Auth type">
          <select
            value={draft.auth_type}
            onChange={e => set('auth_type', e.target.value)}
            className={styles.select}>
            {AUTH_TYPES.map(a => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Credential slot (primary)">
          <input
            type="text"
            value={(draft.auth_config as { credentialSlot?: string })?.credentialSlot ?? ''}
            onChange={e =>
              set('auth_config', {
                ...(draft.auth_config as Record<string, unknown>),
                credentialSlot: e.target.value,
              })
            }
            className={styles.monoInput}
            placeholder="primary"
          />
        </Field>
        {(draft.auth_type === 'custom_header' || draft.auth_type === 'api_key') && (
          <Field label="Header name">
            <input
              type="text"
              value={(draft.auth_config as { headerName?: string })?.headerName ?? ''}
              onChange={e =>
                set('auth_config', {
                  ...(draft.auth_config as Record<string, unknown>),
                  headerName: e.target.value,
                })
              }
              className={styles.monoInput}
              placeholder="x-api-key"
            />
          </Field>
        )}
        <CredentialSlotsEditor
          slots={draft.credentials}
          credentials={credentials.map(c => ({ id: c.id, label: `${c.id} (${c.credential_type})` }))}
          onChange={next => set('credentials', next)}
        />
      </Section>

      <Section
        title="Request schema & mapping"
        subtitle="JSON Schema declares args; request_mapping templates them onto the HTTP request.">
        <JsonField
          label="request_schema"
          value={draft.request_schema}
          onChange={v => set('request_schema', v as Record<string, unknown>)}
          onValidityChange={v => reportFieldValidity('request_schema', v)}
          height="200px"
        />
        <JsonField
          label="request_mapping"
          value={draft.request_mapping}
          onChange={v => set('request_mapping', v as Record<string, unknown>)}
          onValidityChange={v => reportFieldValidity('request_mapping', v)}
          height="220px"
        />
        <JsonField
          label="response_mapping (optional)"
          value={draft.response_mapping ?? {}}
          onChange={v => {
            const obj = v as Record<string, unknown>;
            set('response_mapping', Object.keys(obj).length === 0 ? null : obj);
          }}
          onValidityChange={v => reportFieldValidity('response_mapping', v)}
          height="160px"
        />
      </Section>

      <Section
        title="Security"
        subtitle="Bind tool args to the verified caller so the agent can't act on behalf of someone else.">
        <JsonField
          label="claim_enforcement"
          hint='Map arg names to fields on the calling user, e.g. {"phone": "phone"}. Leave {} for none.'
          value={draft.claim_enforcement ?? {}}
          onChange={v => {
            const obj = v as Record<string, string>;
            set('claim_enforcement', Object.keys(obj).length === 0 ? null : obj);
          }}
          onValidityChange={v => reportFieldValidity('claim_enforcement', v)}
          height="100px"
        />
      </Section>

      <Section
        title="Variables"
        subtitle="Static, non-sensitive values for reuse in templates, referenced as {{variables.<name>}}.">
        <JsonField
          label="variables"
          value={draft.variables ?? {}}
          onChange={v => {
            const obj = v as Record<string, unknown>;
            set('variables', Object.keys(obj).length === 0 ? null : obj);
          }}
          onValidityChange={v => reportFieldValidity('variables', v)}
          height="100px"
        />
      </Section>

      <Section
        title="Caching"
        subtitle="Coming soon — these fields will be honored by the runtime once the cache layer is live.">
        <Field label="Cacheable">
          <input
            type="checkbox"
            checked={draft.cacheable}
            onChange={e => set('cacheable', e.target.checked)}
            className={styles.checkbox}
          />
        </Field>
        {draft.cacheable && (
          <Field label="Cache TTL (ms)">
            <input
              type="number"
              value={draft.cache_ttl_ms}
              onChange={e => set('cache_ttl_ms', Number(e.target.value))}
              className={styles.monoInput}
              min={0}
            />
          </Field>
        )}
      </Section>
    </div>
  );
};

interface SectionProps {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
}

const Section = ({ title, subtitle, children }: SectionProps) => (
  <fieldset className={styles.section}>
    <legend className={styles.legend}>{title}</legend>
    {subtitle && <p className={styles.sectionSubtitle}>{subtitle}</p>}
    <div className={styles.sectionBody}>{children}</div>
  </fieldset>
);

interface FieldProps {
  label: string;
  hint?: string;
  children: ReactNode;
}

const Field = ({ label, hint, children }: FieldProps) => (
  <label className={styles.field}>
    <span className={styles.fieldLabel}>{label}</span>
    {hint && <span className={styles.fieldHint}>{hint}</span>}
    <div className={styles.fieldControl}>{children}</div>
  </label>
);

interface JsonFieldProps {
  label: string;
  hint?: string;
  value: unknown;
  onChange: (parsed: unknown) => void;
  onValidityChange?: (isValid: boolean) => void;
  height: string;
}

const JsonField = ({ label, hint, value, onChange, onValidityChange, height }: JsonFieldProps) => {
  const incomingCanonical = useMemo(() => JSON.stringify(value), [value]);
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [isValid, setIsValid] = useState(true);

  // The canonical (compact) form of what we last reported up. Comparing canonical
  // shapes lets a parent that re-stringifies our parsed value with `null, 2`
  // round-trip without clobbering the user's exact text. Only externally-driven
  // value changes (e.g. seeding from server) produce a different canonical form
  // and trigger a reseed of the editor.
  const lastReportedCanonicalRef = useRef(incomingCanonical);

  useEffect(() => {
    if (incomingCanonical !== lastReportedCanonicalRef.current) {
      lastReportedCanonicalRef.current = incomingCanonical;
      setText(JSON.stringify(value, null, 2));
      setIsValid(true);
      onValidityChange?.(true);
    }
  }, [incomingCanonical, value, onValidityChange]);

  const handleChange = (next: string) => {
    setText(next);
    try {
      const parsed = JSON.parse(next);
      lastReportedCanonicalRef.current = JSON.stringify(parsed);
      setIsValid(true);
      onValidityChange?.(true);
      onChange(parsed);
    } catch {
      setIsValid(false);
      onValidityChange?.(false);
    }
  };

  return (
    <Field label={label} hint={hint}>
      <MonacoJsonEditor value={text} onChange={handleChange} height={height} />
      {!isValid && (
        <p className={styles.jsonError} role="alert">
          Invalid JSON — fix before saving.
        </p>
      )}
    </Field>
  );
};

interface CredentialSlotsEditorProps {
  slots: Record<string, string>;
  credentials: { id: string; label: string }[];
  onChange: (next: Record<string, string>) => void;
}

const CredentialSlotsEditor = ({ slots, credentials, onChange }: CredentialSlotsEditorProps) => {
  const entries = Object.entries(slots);

  return (
    <div>
      <div className={styles.subheading}>Credential slots</div>
      <p className={styles.helpText}>
        Map slot names to credentials created on the Credentials page. Templates reference these as{' '}
        <code className={styles.inlineCode}>{'{{secrets.<slot>}}'}</code>.
      </p>
      <div className={styles.slotList}>
        {entries.map(([slotName, credentialId], idx) => (
          <div key={idx} className={styles.slotRow}>
            <input
              type="text"
              value={slotName}
              onChange={e => {
                const next: Record<string, string> = {};
                entries.forEach(([k, v], i) => {
                  next[i === idx ? e.target.value : k] = v;
                });
                onChange(next);
              }}
              className={styles.slotNameInput}
              placeholder="primary"
            />
            <select
              value={credentialId}
              onChange={e => onChange({ ...slots, [slotName]: e.target.value })}
              className={styles.slotCredentialSelect}>
              <option value="">Pick a credential…</option>
              {credentials.map(c => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => {
                const next = { ...slots };
                delete next[slotName];
                onChange(next);
              }}
              className={styles.removeButton}>
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => {
            const slotName = `slot${entries.length + 1}`;
            onChange({ ...slots, [slotName]: '' });
          }}
          className={styles.linkButton}>
          + Add slot
        </button>
      </div>
    </div>
  );
};

interface AnnotationsEditorProps {
  value: Record<string, boolean>;
  onChange: (next: Record<string, boolean>) => void;
}

const AnnotationsEditor = ({ value, onChange }: AnnotationsEditorProps) => {
  const keys: (keyof typeof labels)[] = [
    'readOnlyHint',
    'destructiveHint',
    'idempotentHint',
    'openWorldHint',
  ];
  const labels = {
    readOnlyHint: 'Read-only (no mutations)',
    destructiveHint: 'Destructive (deletes / hard-to-undo)',
    idempotentHint: 'Idempotent (safe to retry)',
    openWorldHint: 'Calls external system',
  };

  return (
    <fieldset className={styles.annotationGroup}>
      <legend className={styles.subheading}>Annotations</legend>
      <p className={styles.annotationHelp}>
        Set these honestly so calling LLMs reason correctly about side effects.
      </p>
      {keys.map(k => (
        <label key={k} className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={Boolean(value[k])}
            onChange={e => onChange({ ...value, [k]: e.target.checked })}
            className={styles.checkbox}
          />
          {labels[k]}
        </label>
      ))}
    </fieldset>
  );
};
